import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import PayloadEditor from './PayloadEditor'
import { com, kotlin } from '@eidu/form'
import LoadingOverlay from '../../../../components/feedback/LoadingOverlay'
import { useSnackBar } from '../../../../components/feedback/SnackBarProvider'
import PermissionError from '../../PermissionError'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import ScrollFadeContainer from '../../../../components/layout/ScrollFadeContainer'
import PageLayout from '../shared/PageLayout'
import useEntityEdit from '../shared/useEntityEdit'
import { KindEditPageProps } from '../KindPages'
import { getEntityLinkFromResourceKey, getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import { logException } from '../../../../util/Logging'
import computeLabel from '../shared/computeLabel'
import useUploadResource from '../../../../io/useUploadResource'
import useResource from '../../../../io/useResource'
import useRuntimeEntity from '../../../../io/useRuntimeEntity'
import EntityWithLabelAndRelated from '../../../../domain/entity/EntityWithLabelAndRelated'
import RuntimeSelector from './RuntimeSelector'
import PayloadDocumentationButton from './PayloadDocumentation'
import useRuntimeMetadata from '../../../../io/useRuntimeMetadata'
import { AssetPreviewView, useLocalRuntimes } from './AssetPreviewView'
import LogPanel, { useLogEntries } from './LogPanel'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import { getEntityLinkFromRuntimeId, getRuntimeIdFromEntity } from '../../../../io/entityRuntimeIdLink'
import Entity = com.eidu.sharedlib.entity.Entity
import EntityId = com.eidu.sharedlib.entity.EntityId
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import KtMap = kotlin.collections.KtMap
import assetRuntimeLinkKey = com.eidu.sharedlib.entity.assetRuntimeLinkKey
import assetResourceLinkKey = com.eidu.sharedlib.entity.assetResourceLinkKey

const AssetEditPage = ({ entity, entityId, entityType, entityTypes, entityRepository, loading }: KindEditPageProps) => {
  const {
    fields,
    updateField,
    submitting,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerPreSubmit,
  } = useEntityEdit({ entity, entityId, entityType, entityRepository })

  const { upload } = useUploadResource()
  const { showSnackBar } = useSnackBar()

  const existingResourceKey = entity ? getResourceKeyFromEntity(entity) : undefined
  const { content: existingContent, isLoading: contentLoading } = useResource(existingResourceKey)

  const { runtimeEntityId } = useRuntimeEntity(entity)

  const [selectedRuntimeId, setSelectedRuntimeId] = useState<EntityId | undefined>()
  const [runtimeInitialized, setRuntimeInitialized] = useState(false)

  if (runtimeEntityId && !runtimeInitialized) {
    setSelectedRuntimeId(runtimeEntityId)
    setRuntimeInitialized(true)
  }

  const { jsonSchema, documentation } = useRuntimeMetadata(selectedRuntimeId, getKnownKindKey(entityType.kind))

  const [text, setText] = useState<string>('')
  const [initialized, setInitialized] = useState(false)
  const textRef = useRef<string>('')
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const { entries: logEntries, addEntry: addLogEntry, clear: clearLogEntries } = useLogEntries()

  useEffect(() => {
    if (existingContent !== undefined && !initialized) {
      setText(existingContent)
      textRef.current = existingContent
      setInitialized(true)
    }
  }, [existingContent, initialized])

  const handleTextChange = (value: string) => {
    setText(value)
    textRef.current = value
  }

  const preSubmit = useCallback(async () => {
    const content = textRef.current
    const links = []

    if (content || existingResourceKey) {
      try {
        const blob = new Blob([content], { type: 'application/octet-stream' })
        const resourceKey = await upload({ data: blob, extension: 'data' })
        links.push(getEntityLinkFromResourceKey(resourceKey))
      } catch (e) {
        logException(e)
        showSnackBar('Failed to upload payload', 'error')
        throw e
      }
    }

    // Null link values are only valid for updates (to clear a link), not creates

    if (selectedRuntimeId) {
      links.push(getEntityLinkFromRuntimeId(selectedRuntimeId.toString()))
    } else if (entityId) {
      links.push(getEntityLinkFromRuntimeId(null))
    }

    return links
  }, [upload, showSnackBar, existingResourceKey, selectedRuntimeId, entityId])

  useEffect(() => {
    registerPreSubmit(preSubmit)
  }, [registerPreSubmit, preSubmit])

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const [previewEntity, setPreviewEntity] = useState<EntityWithLabelAndRelated | undefined>()

  const buildPreviewEntity = useCallback((): EntityWithLabelAndRelated | undefined => {
    const runtimeId = selectedRuntimeId ?? (entity ? getRuntimeIdFromEntity(entity) : undefined)
    const base = entity?.entity

    if (!runtimeId) return undefined

    const linksMap = base ? new Map(base.links.asJsReadonlyMapView()) : new Map<typeof assetRuntimeLinkKey, string>()
    linksMap.set(assetRuntimeLinkKey, runtimeId.toString())
    linksMap.set(assetResourceLinkKey, 'Blob:' + encodeURIComponent(textRef.current))

    const modified = new Entity(
      base?.id ?? entityIdFromString(crypto.randomUUID()),
      base?.typeId ?? entityType.id,
      base?.valuesByFieldId ?? KtMap.fromJsMap(new Map()),
      KtMap.fromJsMap(linksMap),
      base?.externalValues ?? KtMap.fromJsMap(new Map())
    )
    return new EntityWithLabelAndRelated(
      modified,
      entity?.primaryLabelText ?? '',
      entity?.secondaryLabelText,
      entity?.referencedEntities ?? [],
      []
    )
  }, [entity, selectedRuntimeId, entityType])

  useEffect(() => {
    if (!entity) return
    setPreviewEntity(initialized ? buildPreviewEntity() : entity)
  }, [entity, initialized])

  const label = computeLabel(entityType, fields)

  const inputPanel = (
    <Stack spacing={2} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <ScrollFadeContainer
        sx={{
          maxHeight: '50%',
          flexShrink: 1,
        }}
      >
        <EntityFieldInputGroup
          entityType={entityType}
          entityTypes={entityTypes}
          fields={fields}
          updateField={updateField}
          loading={loading}
        />
      </ScrollFadeContainer>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="h6">Payload</Typography>
          <PayloadDocumentationButton documentation={documentation} />
        </Stack>
        {useLocalRuntimes && getKnownKindKey(entityType.kind) ? (
          <Typography variant="caption" color="warning.main">
            ⚠️ Local Runtime
          </Typography>
        ) : (
          <RuntimeSelector entityTypes={entityTypes} value={selectedRuntimeId} onChange={setSelectedRuntimeId} />
        )}
      </Stack>
      <PayloadEditor
        value={text}
        onChange={handleTextChange}
        editable={!contentLoading}
        placeholder="Enter payload..."
        jsonSchema={jsonSchema}
      />
    </Stack>
  )

  return (
    <>
      <PageLayout
        title={label ?? entityType.name}
        subtitle={label ? entityType.name : undefined}
        showCancel
        showSave
        showDelete={!!entity}
        saveDisabled={!valid}
        onSave={submitEntity}
        onDelete={deleteEntity}
      >
        <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          {inputPanel}
          <Divider orientation="vertical" flexItem />
          <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Typography variant="h6" sx={{ flexShrink: 0 }}>
              Preview
            </Typography>
            {previewEntity ? (
              <AssetPreviewView
                entity={previewEntity}
                entityKind={getKnownKindKey(entityType.kind)}
                entityTypes={entityTypes}
                onValidationChange={(result) => setValidationErrors(result.valid ? [] : result.errors)}
                onRefresh={() => {
                  clearLogEntries()
                  setPreviewEntity(buildPreviewEntity())
                }}
                onLog={addLogEntry}
              />
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {!selectedRuntimeId || !text ? (
                  <Typography color="text.secondary">Select a runtime and add a payload to preview</Typography>
                ) : (
                  <Button variant="outlined" onClick={() => setPreviewEntity(buildPreviewEntity())}>
                    Run Preview
                  </Button>
                )}
              </Box>
            )}
            {validationErrors.map((error) => (
              <Typography key={error} variant="caption" color="error">
                {error}
              </Typography>
            ))}
            {previewEntity && <LogPanel entries={logEntries} onClear={clearLogEntries} />}
          </Stack>
        </Stack>
      </PageLayout>
      <LoadingOverlay isOpen={submitting} />
    </>
  )
}

export default AssetEditPage
